import React from "react";
import { Link, MemoryRouter } from "react-router-dom";
import { motion } from "framer-motion";
import apurvPortrait from "../../assets/images/about/apurv-bordia.png";
import naveenPortrait from "../../assets/images/about/dr-naveen-sharma.png";
import styles from "./AboutUs.module.scss";

// --- Inline SVGs to guarantee compilation ---
const Icons = {
  Target: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <circle cx="12" cy="12" r="6"></circle>
      <circle cx="12" cy="12" r="2"></circle>
    </svg>
  ),
  Shield: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  Cpu: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
      <rect x="9" y="9" width="6" height="6"></rect>
      <line x1="9" y1="1" x2="9" y2="4"></line>
      <line x1="15" y1="1" x2="15" y2="4"></line>
      <line x1="9" y1="20" x2="9" y2="23"></line>
      <line x1="15" y1="20" x2="15" y2="23"></line>
      <line x1="20" y1="9" x2="23" y2="9"></line>
      <line x1="20" y1="14" x2="23" y2="14"></line>
      <line x1="1" y1="9" x2="4" y2="9"></line>
      <line x1="1" y1="14" x2="4" y2="14"></line>
    </svg>
  ),
};

const pageVariants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  },
};
const heroStagger = {
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.06 } },
};
const heroItem = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
  },
};
const listParent = { show: { transition: { staggerChildren: 0.07 } } };
const item = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  },
};
const modeHover = {
  y: -6,
  transition: { type: "spring", stiffness: 400, damping: 25 },
};
const cardHover = {
  y: -8,
  transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
};
const artReveal = {
  hidden: { opacity: 0, scale: 0.95 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.65, ease: [0.25, 0.1, 0.25, 1], delay: 0.12 },
  },
};

const highlights = [
  {
    icon: Icons.Target,
    name: "Institutional focus",
    text: "Neerja Softwares serves one kind of customer—organizations that cannot afford unreliable software. Uptime, audit trails, and clarity in what regulators read.",
  },
  {
    icon: Icons.Shield,
    name: "Certified operations",
    text: "Our information security management is ISO/IEC 27001 certified. Delivery and quality are ISO 9001 certified—audited and continuous.",
  },
  {
    icon: Icons.Cpu,
    name: "All in production",
    text: "Doql is a unified, auditable, enterprise layer for regulated industries—APIs, SaaS, and PaaS. Nothing is a lab demo. Everything is live.",
  },
];

/** Abstract API mesh — Dark Theme variant with Pulse Animation */
const AboutHeroArt = () => (
  <div className={styles["page-about-hero-art"]} aria-hidden>
    <div className={styles["page-about-hero-art-glow"]} />
    <svg
      className={styles["page-about-hero-svg"]}
      viewBox="0 0 440 360"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="ab-line" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.3" />
        </linearGradient>
        <radialGradient id="ab-node" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.4" />
        </radialGradient>
        <filter id="ab-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <path
        d="M40 180 L120 100 L200 200 L300 60 L380 200"
        stroke="url(#ab-line)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.8"
      />
      <path
        d="M120 100 L200 200 L300 60"
        stroke="url(#ab-line)"
        strokeWidth="1.2"
        strokeDasharray="4 6"
        fill="none"
        opacity="0.6"
      />
      <path
        d="M80 260 L180 220 L280 280 L360 240"
        stroke="url(#ab-line)"
        strokeWidth="1.2"
        fill="none"
        opacity="0.6"
      />
      <circle
        className={styles["page-about-hero-node"]}
        cx="120"
        cy="100"
        r="10"
        fill="url(#ab-node)"
        filter="url(#ab-glow)"
      />
      <circle
        className={styles["page-about-hero-node"]}
        cx="200"
        cy="200"
        r="14"
        fill="url(#ab-node)"
        filter="url(#ab-glow)"
      />
      <circle
        className={styles["page-about-hero-node"]}
        cx="300"
        cy="60"
        r="9"
        fill="url(#ab-node)"
        filter="url(#ab-glow)"
      />
      <circle
        className={styles["page-about-hero-node"]}
        cx="380"
        cy="200"
        r="11"
        fill="url(#ab-node)"
        filter="url(#ab-glow)"
      />
      <circle
        className={styles["page-about-hero-node"]}
        cx="180"
        cy="220"
        r="8"
        fill="url(#ab-node)"
        filter="url(#ab-glow)"
      />
      <circle
        className={styles["page-about-hero-node"]}
        cx="280"
        cy="280"
        r="8"
        fill="url(#ab-node)"
        filter="url(#ab-glow)"
      />
      <rect
        x="80"
        y="150"
        width="32"
        height="20"
        rx="4"
        stroke="#4f46e5"
        strokeOpacity="0.25"
        fill="rgba(79,70,229,0.1)"
      />
      <rect
        x="280"
        y="120"
        width="36"
        height="24"
        rx="4"
        stroke="#0ea5e9"
        strokeOpacity="0.25"
        fill="rgba(14,165,233,0.1)"
      />
    </svg>
  </div>
);

const AboutUs = () => {
  return (
    <>
      <div className={`page-page-root ${styles["page-about-page"]}`}>
        <motion.main initial="hidden" animate="show" variants={pageVariants}>
          {/* HERO SECTION */}
          <section
            className={styles["page-about-hero"]}
            aria-labelledby="about-hero-title"
          >
            <div
              className={styles["page-about-hero-ambient"]}
              aria-hidden="true"
            />
            <div
              className={styles["page-about-hero-grid"]}
              aria-hidden="true"
            />

            <div className="sec-container">
              <div className={styles["page-about-hero-row"]}>
                <motion.div
                  className={styles["page-about-hero-inner"]}
                  variants={heroStagger}
                  initial="hidden"
                  animate="show"
                >
                  <motion.span className="page-eyebrow" variants={heroItem}>
                    About us
                  </motion.span>
                  <motion.h1
                    className="page-title"
                    id="about-hero-title"
                    variants={heroItem}
                  >
                    Built by a team that has been shipping{" "}
                    <span className="page-title-accent">
                      enterprise software
                    </span>{" "}
                    for 17 years
                  </motion.h1>
                  <motion.p className="page-lead" variants={heroItem}>
                    Doql is a product of Neerja Softwares Pvt. Ltd. — an ISO
                    27001 and ISO 9001 certified software company with 150+ team
                    members and 250+ clients across banking, healthcare,
                    education, and the public sector.
                  </motion.p>
                  <motion.div
                    className="page-trust-list"
                    role="list"
                    variants={heroItem}
                  >
                    <span className="page-trust-pill" role="listitem">
                      ISO 27001
                    </span>
                    <span className="page-trust-pill" role="listitem">
                      ISO 9001:2015
                    </span>
                    <span className="page-trust-pill" role="listitem">
                      250+ clients
                    </span>
                    <span className="page-trust-pill" role="listitem">
                      BFSI-grade
                    </span>
                  </motion.div>
                </motion.div>

                <motion.div
                  variants={artReveal}
                  initial="hidden"
                  animate="show"
                  className={styles["page-about-hero-art-wrap"]}
                >
                  <AboutHeroArt />
                </motion.div>
              </div>
            </div>
          </section>

          {/* MAIN CONTENT */}
          <div className="page-main">
            <div className="sec-container">
              {/* Highlights Overlap Grid */}
              <motion.div
                className={styles["page-highlights-grid"]}
                role="list"
                variants={listParent}
                initial="hidden"
                animate="show"
              >
                {highlights.map((h, i) => {
                  const Ic = h.icon;
                  return (
                    <motion.div
                      key={h.name}
                      className={styles["page-highlight-card"]}
                      role="listitem"
                      variants={item}
                      whileHover={cardHover}
                    >
                      <span
                        className={styles["page-highlight-index"]}
                        aria-hidden
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div
                        className={styles["page-highlight-icon"]}
                        aria-hidden
                      >
                        <Ic />
                      </div>
                      <strong className={styles["page-highlight-name"]}>
                        {h.name}
                      </strong>
                      <p className={styles["page-highlight-text"]}>{h.text}</p>
                    </motion.div>
                  );
                })}
              </motion.div>

              {/* Compliance Banner */}
              <p className="page-compliance-banner">
                <Icons.Shield />
                <span>
                  A Neerja Softwares product—ISO 27001 and ISO 9001 certified.
                  The Doql platform operates to SOC 2 Type II and PCI-DSS Level
                  1 standards and is aligned to DPDPA.
                </span>
              </p>

              {/* Founders Section */}
              <div id="founders">
                <header className="page-section-head">
                  <span className={styles["page-section-eyebrow"]}>
                    Founders
                  </span>
                  <h2 className="page-section-title">
                    The team behind the platform
                  </h2>
                  <p className="page-section-desc">
                    Leadership with deep experience in building and shipping
                    regulated, enterprise software.
                  </p>
                </header>

                <motion.div
                  className={styles["page-founders-grid"]}
                  role="list"
                  variants={listParent}
                  initial="hidden"
                  animate="show"
                >
                  <motion.article
                    className={styles["page-founder-tile"]}
                    role="listitem"
                    variants={item}
                    whileHover={cardHover}
                  >
                    <div className={styles["page-founder-photo"]}>
                      <img
                        className={styles["page-founder-img"]}
                        src={apurvPortrait}
                        alt="Mr. Apurv Bordia"
                        width={300}
                        height={300}
                        loading="lazy"
                      />
                      <div
                        className={styles["page-founder-overlay"]}
                        aria-hidden="true"
                      />
                    </div>
                    <div className={styles["page-founder-content"]}>
                      <h3 className={styles["page-founder-name"]}>
                        Mr. Apurv Bordia
                      </h3>
                      <p className={styles["page-founder-role"]}>Founder</p>
                      <ul className={styles["page-founder-bullets"]}>
                        <li>17+ years of experience in technology companies</li>
                        <li>
                          Successfully built and exited multiple tech ventures
                        </li>
                      </ul>
                      <p className={styles["page-founder-foot"]}>
                        MIS in Information Technologies from the University of
                        Connecticut, USA (double major in information systems,
                        credit card encryption, and marketing).
                      </p>
                    </div>
                  </motion.article>

                  <motion.article
                    className={styles["page-founder-tile"]}
                    role="listitem"
                    variants={item}
                    whileHover={cardHover}
                  >
                    <div className={styles["page-founder-photo"]}>
                      <img
                        className={styles["page-founder-img"]}
                        src={naveenPortrait}
                        alt="Dr. Naveen Kumar Sharma"
                        width={300}
                        height={300}
                        loading="lazy"
                      />
                      <div
                        className={styles["page-founder-overlay"]}
                        aria-hidden="true"
                      />
                    </div>
                    <div className={styles["page-founder-content"]}>
                      <h3 className={styles["page-founder-name"]}>
                        Dr. Naveen Kumar Sharma
                      </h3>
                      <p className={styles["page-founder-role"]}>Co-founder</p>
                      <p className={styles["page-founder-creds"]}>
                        PhD in Cyber Security · Cyber Law graduate · Author,
                        DPDPA 2023: From Chaos to Compliance · AI Visionary of
                        the Year 2025
                      </p>
                      <p className={styles["page-founder-body"]}>
                        His academic and authorial work in cyber security and
                        data protection law shapes the platform&apos;s posture;
                        his conviction anchors what we build.
                      </p>
                      <blockquote className="page-quote">
                        &ldquo;AI was built by humanity. It should serve all of
                        humanity.&rdquo; — Dr. Naveen Kumar Sharma
                      </blockquote>
                    </div>
                  </motion.article>
                </motion.div>
              </div>

              {/* Bottom CTA */}
              <div
                className="page-bottom-cta"
                role="group"
                aria-label="Next steps"
              >
                <p>
                  Align the product suite, pricing, and implementation with your
                  organization.
                </p>
                <div className="page-cta-row">
                  <Link to="/products" className="page-btn page-btn-primary">
                    View product suite →
                  </Link>
                  <Link
                    to="/pricing"
                    className={`page-btn ${styles["page-btn-secondary"]}`}
                  >
                    View pricing
                  </Link>
                </div>
                <p className="page-footer-note">
                  <Link to="/contact" className={styles["page-footer-link"]}>
                    Contact us for deployment, regions, and SLAs →
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </motion.main>
      </div>
    </>
  );
};

export default AboutUs;
